import { GQL_ID } from '@@types'
import { useEffect, useRef } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useFetchShopProducts, useProductSort } from '@entities/Product'

import { useAddressStore } from '@features/shipping-methods'

import NotFoundComponent from '@widgets/not-found/NotFoundComponent'
import { ProductCard } from '@widgets/product-preview'

import { QUERY_NAMES } from '@shared/contants'
import { useFilters } from '@shared/hooks'
import { useAnalytics } from '@shared/lib/analytics'
import { DynamicFilters } from '@shared/types'
import CardSkeleton from '@shared/ui/card-skeleton'
import { mapFiltersToApi } from '@shared/uttils/mapFiltersToApi'

import styles from './styles.module.scss'


const LIMIT = 12

type ProductsProps = {
  onTotalChange?: (total: number | null) => void;
  categoryId?: GQL_ID;
  loadingCategory?: boolean;
  /** Название списка для аналитики */
  listName?: string;
  /** ID списка для аналитики */
  listId?: string;
};

function ProductsLoading () {
  return (
    <div className={styles.root}>
      {Array.from({ length: 8 }).map((_, index) => (
        <CardSkeleton key={index} />
      ))}
    </div>
  )
}

export default function Products ({ onTotalChange, categoryId, loadingCategory, listName, listId }: ProductsProps) {
  const { sortConfig } = useProductSort()
  const { filters } = useFilters<DynamicFilters>({
    groupKey: QUERY_NAMES.PRODUCT_FILTERS,
  })
  const apiFilters = mapFiltersToApi(filters)
  const { shopId } = useAddressStore()
  const { viewItemList } = useAnalytics()

  // Track if we've already sent view_item_list for current items
  const trackedItemsRef = useRef<string>('')

  const {
    products: items,
    pageInfo,
    loading,
    loadingMore,
    hasMore,
    fetchNextPage,
    clearPagination,
  } = useFetchShopProducts({
    shopId,
    categoryId,
    page: 1,
    limit: LIMIT,
    sort: {
      fieldName: sortConfig?.fieldName,
      direction: sortConfig?.direction,
    },
    filter: apiFilters
  })

  const total = pageInfo?.total ?? items.length ?? 0

  // Определяем название списка для аналитики
  const analyticsListName = listName || 'Category Products'
  const analyticsListId = listId || `category_${categoryId}`

  // Track view_item_list when items change
  useEffect(() => {
    if (items.length && !loading) {
      const itemsKey = items.map(p => p.id).join(',')
      if (trackedItemsRef.current !== itemsKey) {
        viewItemList(items, analyticsListName, analyticsListId)
        trackedItemsRef.current = itemsKey
      }
    }
  }, [ items, loading, viewItemList, analyticsListName, analyticsListId ])

  useEffect(() => {
    onTotalChange?.(total)
  }, [ total, onTotalChange ])

  useEffect(() => {
    clearPagination()
    trackedItemsRef.current = '' // Reset tracking on pagination clear
  }, [ categoryId, filters, sortConfig, clearPagination ])

  if ((loading && items.length === 0) || loadingCategory || !shopId) {
    return <ProductsLoading />
  }

  if (!categoryId) {
    return (
      <NotFoundComponent />
    )
  }

  return (
    <InfiniteScroll
      dataLength={items.length}
      next={fetchNextPage}
      hasMore={hasMore}
      loader={loadingMore && <ProductsLoading />}
    >
      <section className={styles.root}>
        {items.map((p) => (
          <ProductCard
            key={p.id}
            className={styles.card}
            data={p}
            listName={analyticsListName}
            listId={analyticsListId}
          />
        ))}
      </section>
    </InfiniteScroll>
  )
}
